import { RestSchemas } from '@/contexts/QueryProvider'
import { embeddedAppsByKey } from '@/embedded-apps/embeddedAppManifest'
import { AppTypesEnum } from '@/util/appTypes'
import { getAppLaunchPath } from '@/util/getAppLaunchPath'
import { UserLinkedProject } from './useUserLinkedProjects'

// Manual type override because the API returns camelCase keys not reflected in the generated schema.
export type ProjectWorkspace = Omit<
  RestSchemas['Messages.Projects.UserLinkedProjectWorkspace'],
  'app_key' | 'app_id' | 'has_access' | 'approval_date'
> & {
  appKey: string
  appId: number
  hasAccess: boolean
  approvalDate?: string | null
  canEditWorkspace: boolean
}

export function workspaceMatchesSearch(workspace: ProjectWorkspace, search: string): boolean {
  return workspace.application.toLowerCase().includes(search)
}

export function projectNameMatchesSearch(project: UserLinkedProject, search: string): boolean {
  return project.name.toLowerCase().includes(search)
}

export function projectMatchesSearch(project: UserLinkedProject, search: string): boolean {
  return (
    projectNameMatchesSearch(project, search) ||
    project.organisation.toLowerCase().includes(search)
  )
}

export function projectOrWorkspaceMatchesSearch(project: UserLinkedProject, search: string): boolean {
  if (!search) {
    return true
  }

  if (projectMatchesSearch(project, search)) {
    return true
  }

  return project.workspaces.some((workspace) => workspaceMatchesSearch(workspace, search))
}

export function filterProjectWorkspaces(project: UserLinkedProject, search: string): ProjectWorkspace[] {
  if (!search) {
    return project.workspaces
  }

  if (projectNameMatchesSearch(project, search)) {
    return project.workspaces
  }

  return project.workspaces.filter((workspace) => workspaceMatchesSearch(workspace, search))
}

/** True when the project is surfaced by a workspace name match, not by project/org metadata. */
export function hasWorkspaceOnlySearchMatch(project: UserLinkedProject, search: string): boolean {
  const normalizedSearch = search.trim().toLowerCase()
  if (!normalizedSearch) {
    return false
  }

  if (projectMatchesSearch(project, normalizedSearch)) {
    return false
  }

  return project.workspaces.some((workspace) => workspaceMatchesSearch(workspace, normalizedSearch))
}

export function getProjectWorkspaceAppKey(workspace: ProjectWorkspace): string {
  return workspace.appKey
}

export function getProjectWorkspaceAppId(workspace: ProjectWorkspace): number {
  return workspace.appId
}

export function getWorkspaceHasAccess(workspace: ProjectWorkspace): boolean {
  return workspace.hasAccess
}

export function getWorkspaceApprovalDate(workspace: ProjectWorkspace): string | null | undefined {
  return workspace.approvalDate
}

export function getWorkspaceArchived(workspace: ProjectWorkspace): boolean {
  return workspace.archived ?? false
}

export function getProjectWorkspacePaths(
  workspace: ProjectWorkspace,
  projectId: number,
  organisationId: number
) {
  const appKey = getProjectWorkspaceAppKey(workspace)
  const appId = getProjectWorkspaceAppId(workspace)

  return {
    launchAppPath: getAppLaunchPath(
      inferAppTypesFromAppKey(appKey),
      appKey,
      appId,
      projectId,
      workspace.workspaceId,
      organisationId
    ),
    workspaceDetailsPath: `/workspaces/${workspace.workspaceId}`,
  }
}

export function inferAppTypesFromAppKey(appKey: string): number {
  if (embeddedAppsByKey[appKey]) {
    return AppTypesEnum['Embedded React']
  }

  return AppTypesEnum['Catalog']
}
